import { useEffect, useState } from 'react';
import { EvaluationCase } from './types';
import { findDuplicate } from './utils';

export type CaseDraft = {
    /** the case being edited, or null when a new one is being written */
    index: number | null;
};

export type UseCaseEditor = {
    /** the declaration as text, which is what is edited */
    text: string;
    setText: (next: string) => void;
    /** the verdict the case asks for */
    isValid: boolean;
    setIsValid: (next: boolean) => void;
    /** the one thing that can be wrong with the draft */
    error: string;
    /** parse, check and hand the case up; nothing happens if it does not hold */
    commit: () => void;
};

const BLANK = '{\n    \n}';

/**
 * The declaration editor behind the add/edit sheet.
 *
 * Separate from the sheet because the sheet is an antd `<Modal>`, which this
 * repo's jest setup cannot render (see REACT18_UPGRADE_PLAN.md) — every rule
 * about what a case may be therefore lives here, where it can be tested.
 *
 * The rules are the API's: `declaration` must be a JSON **object**, and two
 * cases may not share one. Both are checked before the request rather than
 * after it, so a typo does not cost a round trip.
 */
const useCaseEditor = ({
    cases,
    draft,
    onCommit,
}: {
    cases: EvaluationCase[];
    draft: CaseDraft | null;
    onCommit: (next: EvaluationCase, index: number | null) => void;
}): UseCaseEditor => {
    const editing = draft && draft.index !== null ? cases[draft.index] : undefined;

    const [text, setText] = useState<string>(BLANK);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    // opening the sheet — on a case or on nothing — starts it from that case
    useEffect(() => {
        if (!draft) return;
        setText(editing ? JSON.stringify(editing.declaration, null, 4) : BLANK);
        setIsValid(editing ? editing.is_valid : true);
        setError('');
        // `editing` is derived from the two below; keying on the object itself
        // would reset the editor on every re-render of the list
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft, draft?.index]);

    /** typing clears the error: it was about text that no longer exists */
    const change = (next: string) => {
        setText(next);
        if (error) setError('');
    };

    const commit = () => {
        if (!text.trim()) {
            setError('Write the declaration this case should be judged on.');
            return;
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(text);
        } catch (e) {
            setError('Invalid JSON. Please correct it before saving.');
            return;
        }

        /* JSON.parse is happy with `4`, `"x"` and `[…]` — all valid JSON, none
           of them a declaration. The API rejects them, so the sheet does. */
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            setError('The declaration must be a JSON object.');
            return;
        }

        if (findDuplicate(cases, parsed, draft?.index ?? undefined) !== -1) {
            setError('Another case is already using this declaration.');
            return;
        }

        onCommit({ declaration: parsed as Record<string, any>, is_valid: isValid }, draft?.index ?? null);
    };

    return { text, setText: change, isValid, setIsValid, error, commit };
};

export default useCaseEditor;
